import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../../config/axios';
import { InputDiv, Label, Input, SearchOptions, Option } from '../../../../components/styles/select-style';
import AddCompanyModal from '../../../../components/AddCompanyModal';

interface ItemType {
  id: number;
}

interface CompanySelectProps<T extends ItemType> {
  label: string;
  apiUrl: string;
  displayField: keyof T;
  queryParams?: Record<string, string | number>;
  value: T;
  onChange: (item: T) => void;
  showDocument?: boolean;
  allowCreate?: boolean;
}

const companyCache = new Map<string, any[]>();
const companyFetchPromises = new Map<string, Promise<any[]>>();

const CompanySelect = <T extends ItemType>({
  label,
  apiUrl,
  displayField,
  queryParams = {},
  value,
  onChange,
  showDocument = true,
  allowCreate = false,
}: CompanySelectProps<T>) => {
  const cacheKey = `${apiUrl}|${JSON.stringify(queryParams)}`;



  const [items, setItems] = useState<T[]>([]);
  const [allCompanies, setAllCompanies] = useState<T[]>([]);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);
  const getLabelMain = (item: any): string => {
    if (!item) return '';
    const fullItem = allCompanies.find(c => c.id === item.id) || item;
    if (!fullItem || !fullItem.isMain) return '';
    const tipo = fullItem.tipoEstabelecimento || (fullItem.cnpj ? (fullItem.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
    return tipo ? ` - ${tipo}` : '';
  };

  const getDisplayName = (val: any): string => {
    if (!val) return '';
    const fullItem = allCompanies.find(c => c.id === val.id) || val;
    const baseName = (fullItem[displayField] || fullItem.companyName || fullItem.contactName || fullItem.name || fullItem.fantasia || '') as string;
    return `${baseName}${getLabelMain(fullItem)}`;
  };

  const [inputItem, setInputItem] = useState<string>(getDisplayName(value));
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionsRef = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setInputItem(getDisplayName(value));
  }, [value, displayField, allCompanies]);

  const valueId = value.id;
  const hydratedIdRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (valueId && allCompanies.length > 0 && hydratedIdRef.current !== valueId) {
      const found = allCompanies.find(item => item.id === valueId);
      if (found) {
        const currentValue = value as Record<string, any>;
        const hasMissingKeys = Object.keys(found).some(key => !(key in currentValue));
        if (hasMissingKeys) {
          hydratedIdRef.current = valueId;
          onChangeRef.current(found);
        }
      }
    }
  }, [valueId, allCompanies, value]);

  useEffect(() => {
    const fetchCompanies = async () => {
      setIsLoading(true);

      if (companyCache.has(cacheKey)) {
        const cached = companyCache.get(cacheKey) as T[];
        setAllCompanies(cached);
        setItems(cached);
        setIsLoading(false);
        return;
      }

      let fetchPromise = companyFetchPromises.get(cacheKey);
      if (!fetchPromise) {
        fetchPromise = (async () => {
          const response = await axios.get<any>(apiUrl, {
            params: { ...queryParams, limit: 1000 },
          });

          const dataArray = Array.isArray(response.data) ? response.data : (response.data?.data || []);

          if (!Array.isArray(dataArray)) {
            return [] as T[];
          }

          return dataArray as T[];
        })();

        companyFetchPromises.set(cacheKey, fetchPromise);
      }

      try {
        const companies = await fetchPromise;
        companyCache.set(cacheKey, companies);
        setAllCompanies(companies);
        setItems(companies);
      } catch (error) {
        setAllCompanies([]);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, [cacheKey, label]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        optionsRef.current &&
        inputRef.current &&
        !optionsRef.current.contains(target) &&
        !inputRef.current.contains(target)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputItem(newValue);

    const filterCriteria = newValue.toLowerCase();

    if (newValue === '') {
      setItems(allCompanies);
    } else {
      const cleanNewValue = newValue.replace(/\D/g, '');
      const filteredItems = allCompanies.filter(item => {
        const name = (item[displayField] || (item as any).contactName || (item as any).name || (item as any).companyName || '').toLowerCase();
        const cnpj = ((item as any).cnpj || '').toLowerCase();
        const cleanCnpj = cnpj.replace(/\D/g, '');
        const cpf = ((item as any).cpf || '').toLowerCase();
        const cleanCpf = cpf.replace(/\D/g, '');

        return name.includes(filterCriteria) ||
               cnpj.includes(filterCriteria) ||
               (cleanNewValue && cleanCnpj.includes(cleanNewValue)) ||
               cpf.includes(filterCriteria) ||
               (cleanNewValue && cleanCpf.includes(cleanNewValue));
      });
      setItems(filteredItems);
    }

    setSelectedIndex(0);

    onChange({ id: 0, [displayField]: newValue } as unknown as T);
    setShowOptions(true);
  };

  const handleSelectItem = (item: T) => {
    setInputItem(getDisplayName(item));
    setShowOptions(false);
    onChange(item);
  };

  const handleCreateCompanyClick = () => {
    setIsCreatingCompany(true);
    setShowOptions(false);
  };

  const handleSetOptionByKey = (e: React.KeyboardEvent) => {
    const totalOptions = items.length + (allowCreate ? 1 : 0);
    if (e.key === 'ArrowDown' && selectedIndex < totalOptions - 1) {
      setSelectedIndex(prev => prev + 1);
      e.preventDefault();
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      setSelectedIndex(prev => prev - 1);
      e.preventDefault();
    }
    if (e.key === 'Enter') {
      if (allowCreate && selectedIndex === items.length) {
        handleCreateCompanyClick();
      } else if (items[selectedIndex]) {
        handleSelectItem(items[selectedIndex]);
      }
      e.preventDefault();
    }
    if (e.key === 'Tab') {
      setShowOptions(false);
    }
  };

  if (isLoading) {
    return (
      <InputDiv>
        <Label>{label}</Label>
        <Input type='text' value='Carregando...' readOnly />
      </InputDiv>
    );
  }

  return (
    <InputDiv>
      <Label>{label}</Label>
      <Input
        ref={inputRef}
        type='text'
        placeholder='Selecione'
        value={inputItem}
        onFocus={() => {
          setShowOptions(true);
        }}
        onChange={handleChangeInput}
        onKeyDown={handleSetOptionByKey}
      />
      {showOptions && (items.length > 0 || (allowCreate && inputItem)) && (
        <SearchOptions ref={optionsRef}>
          {items.map((s, index) =>
            <Option
              key={s.id}
              onClick={() => handleSelectItem(s)}
              isSelected={index === selectedIndex}
            >
              {showDocument ? (
                <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                  <span style={{ fontWeight: '500', fontSize: '14px' }}>
                    {s[displayField] as React.ReactNode}
                    {getLabelMain(s)}
                  </span>
                  <span style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                    {(s as any).cnpj || (s as any).cpf || 'Documento não informado'}
                  </span>
                </div>
              ) : (
                <span style={{ fontWeight: '500', fontSize: '14px' }}>
                  {getDisplayName(s)}
                </span>
              )}
            </Option>
          )}
          {allowCreate && inputItem && (
            <Option
              onClick={handleCreateCompanyClick}
              isSelected={selectedIndex === items.length}
              style={{ borderTop: '1px solid #eee', color: '#0090D3', fontWeight: '600' }}
            >
              <div style={{ padding: '8px 0' }}>
                {`+ Criar cadastro para "${inputItem}"`}
              </div>
            </Option>
          )}
        </SearchOptions>
      )}

      {isCreatingCompany && (
        <AddCompanyModal
          initialName={inputItem.replace(/[0-9./-]/g, '').trim()}
          initialCnpjCpf={inputItem.replace(/[^0-9]/g, '')}
          initialType={label === 'Matriz/Fornecedor' ? 'Fornecedor' : label === 'Transportadora' ? 'Transportadora' : 'Cliente'}
          onSuccess={(newCompany) => {
            setAllCompanies(prev => [newCompany, ...prev]);
            setItems(prev => [newCompany, ...prev]);
            handleSelectItem(newCompany);
            setIsCreatingCompany(false);
          }}
          onCancel={() => setIsCreatingCompany(false)}
        />
      )}
    </InputDiv>
  );
};

export default CompanySelect;
